// externals

// components
import { Badge } from '@interstate/components/Badge';
import { Box } from '@interstate/components/Box';
import { LinkChip } from '@interstate/components/Chip';
import { DocumentTextIcon } from '@interstate/components/Icons/DocumentTextIcon';
// selectors
import * as creditAppSearchActionCreators from '@makemydeal/dr-dash-store/actions/creditAppSearchActionCreators';
import * as dealLifeCycleActionCreators from '@makemydeal/dr-dash-store/actions/dealLifeCycleActionCreators';
import * as employeeDetailsActionCreators from '@makemydeal/dr-dash-store/actions/employeeDetailsActionCreators';
import * as creditAppSelectors from '@makemydeal/dr-dash-store/selectors/creditAppSelectors';
import * as dealLifecycleSelectors from '@makemydeal/dr-dash-store/selectors/dealLifecycleSelectors';
import * as dealXgDetailsSelectors from '@makemydeal/dr-dash-store/selectors/dealXgDetailsSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as quoteSelectors from '@makemydeal/dr-dash-store/selectors/quoteSelectors';
import { shortDateString } from '@makemydeal/dr-dash-store/utils/dateFormatters';
// constants
import { CASH } from '@makemydeal/dr-platform-types';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
// hooks
import { useDispatch, useSelector } from 'react-redux';
import { APPROVED, DELIVERED, SIGNED, SOLD } from '../constants';
// styles
import { ContractStatusBadgeContainer } from '../contracting/ContractStatusBadgeContainer';
import { CreditDecisionStatusBadge } from '../creditAppDecision/CreditDecisionStatusBadge';
import { dealLockHooks } from '../hooks';
import { DashboardLifecycleBaseContainer } from './DealDatesAndStatus.style';
import { DealLifecycleModal } from './DealLifecycleModal.interstate';
import { InformationCircleIcon } from '@interstate/components/Icons';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Popover } from '@interstate/components/Popover';
import * as pushToDmsSelectors from '@makemydeal/dr-dash-store/selectors/pushToDmsSelectors';
import DmsExportPopoverContent from '../dealActivity/pushToDms/DmsExportStatusPopover';
import { CDMS } from '@makemydeal/dr-dash-store/constants';

const STATUSES = {
    IN_PROGRESS: 'inprogress',
    SOLD: 'sold',
    LOST: 'lost'
};

export const DealDatesAndStatus = ({ children }: React.PropsWithChildren) => {
    const dispatch = useDispatch();
    const isEcommerceDeal = useSelector(dealXgDetailsSelectors.getIsEcommerceDeal);
    const dealLifecycle = useSelector(dealLifecycleSelectors.getDealLifecycle);
    const status = dealLifecycle.status || '';
    const statusLowerCased = status.toLowerCase();
    const { soldDate, approvedDate, contractSignedDate, deliveredDate } = dealLifecycle;
    const showDealLifeCycleModal = useSelector(dealLifecycleSelectors.getShowDealLifeCycleModal);
    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);
    const isMVSalesTeamSlideoutEnabled = useSelector(featureToggleSelectors.isMVSalesTeamSlideoutEnabled);
    const isEnableMVCreditAppSearchAndMerge = useSelector(featureToggleSelectors.isEnableMVCreditAppSearchAndMerge);
    const isDMSExportStatusDisplayEnabled = useSelector(featureToggleSelectors.isDMSExportStatusDisplayEnabled);
    const isDealXgRecordMissed = useSelector(offerReduxSelectors.getIsDealXgRecordMissed);
    const isQuoteMode = useSelector(quoteSelectors.getQuoteMode);
    const dealerName = useSelector(dealerSelectors.getDealerName);
    const hasDealBeenAccepted = useSelector(offerSelectors.hasDealBeenAccepted);
    const isTraditionalMode = useSelector(dealerSelectors.getIsTraditionalMode);
    const showAcceptedBadge = hasDealBeenAccepted && isTraditionalMode;

    const creditAppId = useSelector(creditAppSelectors.getCreditAppReferenceId);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isLocked = dealLockHooks.useIsDealLocked();
    const dmsExportState = useSelector(pushToDmsSelectors.getDmsExportState);
    const dmsExportStatus = dmsExportState?.status;
    const dmsIntegrationToggle = useSelector(dealerSelectors.getDMSIntegrationToggle);

    const theme = useInterstateTheme();

    const SIGNED_LABEL = SIGNED;

    const getDashboardDate = () => {
        if (deliveredDate) {
            return `${DELIVERED}: ${shortDateString(new Date(deliveredDate))}`;
        } else if (contractSignedDate) {
            return `${SIGNED_LABEL}: ${shortDateString(new Date(contractSignedDate))}`;
        } else if (approvedDate) {
            return `${APPROVED}: ${shortDateString(new Date(approvedDate))}`;
        } else if (soldDate) {
            return `${SOLD}: ${shortDateString(new Date(soldDate))}`;
        }
        return null;
    };

    // Create a handler for showing the modal
    const handleShowLifecycleModal = (value: boolean) => {
        dispatch(dealLifeCycleActionCreators.showDealLifeCycleModal(value));
    };

    const value = getDashboardDate();
    return (
        <div data-testid="dr-dash-deal-status-container" className="deal-status-container">
            <DashboardLifecycleBaseContainer className="dashboard-container">
                <ContractStatusBadgeContainer />
                {value && (
                    <Badge
                        data-testid="deal-date"
                        sx={{ marginRight: '0.7rem' }}
                        startIcon={<DocumentTextIcon />}
                        variant={!isDealCentralExperience ? 'transparent' : undefined}
                    >
                        {getDashboardDate()}
                    </Badge>
                )}
                {isEnableMVCreditAppSearchAndMerge && !creditAppId && offerType !== CASH && (
                    <LinkChip
                        data-testid="credit-app-search-chip"
                        sx={{ marginRight: '0.7rem' }}
                        label="No Credit App Attached"
                        onClick={() => {
                            if (isDealXgRecordMissed) return;
                            dispatch(creditAppSearchActionCreators.openCreditAppSearchSlideout());
                        }}
                        disabled={isDealXgRecordMissed}
                    />
                )}
                {isEcommerceDeal && (
                    <Badge
                        data-testid="is-ecommerce-deal"
                        sx={{ marginRight: '0.7rem' }}
                        variant={!isDealCentralExperience ? 'transparent' : undefined}
                    >
                        Ecommerce
                    </Badge>
                )}
                <CreditDecisionStatusBadge />
                {isMVSalesTeamSlideoutEnabled && (
                    <LinkChip
                        data-testid="deal-employee-details-chip"
                        sx={{ marginRight: '0.7rem' }}
                        label="Sales Team"
                        onClick={() => {
                            if (isDealXgRecordMissed) return;
                            dispatch(employeeDetailsActionCreators.openEmployeeDetailsSlideout());
                        }}
                        disabled={isDealXgRecordMissed}
                    />
                )}
                {isQuoteMode && dealerName && (
                    <LinkChip
                        data-testid="deal-dealership-chip"
                        sx={{ marginRight: '0.7rem' }}
                        label={`Dealership: ${dealerName}`}
                        disabled={isDealXgRecordMissed}
                    />
                )}
                {isLocked && (
                    <Badge data-testid="deal-finalized-badge" variant="transparent" sx={{ marginRight: '0.7rem' }}>
                        Deal Finalized
                    </Badge>
                )}
                <LinkChip
                    data-testid="deal-status-value"
                    label={`${statusLowerCased === STATUSES.IN_PROGRESS ? 'In Progress' : status}`}
                    onClick={() => {
                        if (isDealXgRecordMissed) return;
                        handleShowLifecycleModal(true);
                    }}
                    disabled={isDealXgRecordMissed}
                    sx={{ marginRight: '0.7rem' }}
                />
                {isDMSExportStatusDisplayEnabled && dmsExportState && dmsIntegrationToggle === CDMS && (
                    <Badge
                        endIcon={
                            <Box
                                component="span"
                                display="inline-flex"
                                sx={{
                                    '& .ids-popover-anchor-container': {
                                        marginLeft: '-19px'
                                    }
                                }}
                            >
                                <Popover
                                    popoverContent={<DmsExportPopoverContent exportState={dmsExportState} />}
                                    trigger="hover"
                                    position="bottom-start"
                                    variant="withPointer"
                                    header={dmsExportStatus === 'success' ? 'Export Successful' : 'Export Failed'}
                                >
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            paddingLeft: '19px'
                                        }}
                                    >
                                        <InformationCircleIcon
                                            style={{ marginTop: '2px' }}
                                            color={theme.tokens.ColorSurfaceButtonPrimaryRest}
                                        />
                                    </span>
                                </Popover>
                            </Box>
                        }
                        data-testid="dms-export-status-badge"
                        variant={dmsExportStatus === 'success' ? 'success' : 'error'}
                        sx={children ? { marginRight: '0.7rem' } : undefined}
                    >
                        {dmsExportStatus === 'success' ? 'DMS Exported' : 'DMS Export Failed'}
                    </Badge>
                )}
                {children}
                {showAcceptedBadge && (
                    <Badge variant="success" data-testid="deal-accepted" sx={children ? { marginLeft: '0.7rem' } : undefined}>
                        Accepted
                    </Badge>
                )}
            </DashboardLifecycleBaseContainer>
            <DealLifecycleModal show={showDealLifeCycleModal} dealLifecycle={dealLifecycle} />
        </div>
    );
};
